import React from 'react'
import { Container, Row, Col, Label, Button } from 'reactstrap'
import '../../css/PopOverController.css'

/**
 * Turns anything handed to showError() into something this modal can
 * render. There are only ever two kinds of error, and they are answered
 * differently on purpose:
 *
 * 1. We worked out something was wrong ourselves, without ever asking the
 *    server (a failed form validation, a missing selection). The caller
 *    passes the sentence it wants shown, and that is what is shown.
 * 2. We asked the server and it said no. Then whatever the server replied
 *    is what the user sees - we do not second-guess it. Our own routes
 *    reply {err: 'a sentence'}; a route that threw instead falls through
 *    to Express's default handler (this app installs no error middleware
 *    of its own), which replies with a whole HTML page, so that HTML is
 *    rendered as-is rather than stripped down to nothing readable.
 *
 * This is the only place that knows what a request error looks like -
 * render() below just renders whatever it is given, nothing more.
 * @param {string|object} err a message we chose, or a caught request error
 * @return {string|object} a string, or a React node for a server HTML page
 */
function errorMessage(err) {
    if (typeof err === 'string') {
        return err
    }

    const data = err && err.response && err.response.data
    if (typeof data === 'string') {
        return <div dangerouslySetInnerHTML={{__html: data}} />
    }
    if (data && typeof data.err === 'string') {
        return data.err
    }
    if (data) {
        // The server answered with some other JSON shape. Showing it beats
        // the "[object Object]" this used to render.
        return JSON.stringify(data)
    }

    // The request went out but nothing came back - dropped connection,
    // timeout, or blocked by the browser.
    if (err && err.request) {
        return 'Could not connect.'
    }

    // Not a request failure at all: a plain JS error thrown in the .then()
    // after the request already succeeded. Saying "could not connect" here
    // would describe a problem that never happened.
    return (err && err.message) || 'Could not connect.'
}

class AlertModalModel {
    constructor(title='', message='') {
        this.title = title
        this.message = message
        this.bottomRightRow = []
    }

    setTitle(newTitle) {
        this.title = newTitle
        return this
    }

    setMessage(newMessage) {
        this.message = newMessage
        return this
    }

    addToBottomRightRow(element) {
        this.bottomRightRow.push(element)
        return this
    }

    addBottomRightBotton(title, onClick) {
        return this.addToBottomRightRow(
            <Button
            style={{'marginLeft': '20px'}}
            onClick={(e) => onClick()}>{title}</Button>
        )
    }

    addYesButton(onClick) {
        return this.addBottomRightBotton('Yes', onClick)
    }

    addNoButton(onClick) {
        return this.addBottomRightBotton('No', onClick)
    }

    addOkButton(onClick) {
        return this.addBottomRightBotton('Ok', onClick)
    }

    addCancelButton(onClick) {
        return this.addBottomRightBotton('Cancel', onClick)
    }
}

class AlertModalController extends React.Component{

    constructor(props) {
        super(props)
        this.containerRef = React.createRef()
    }

    // The modal is a plain overlay div, not a real focus-trapping dialog -
    // nothing moves keyboard focus into it on its own, so without this the
    // page underneath keeps focus and stays editable while the modal is
    // up. Focusing the FIRST bottom-row button (rather than the last) puts
    // focus on whichever action was added first - e.g. addBottomRightBotton
    // is called for the modal's own primary action before addCancelButton,
    // so that's the one that ends up focused, with Cancel still reachable
    // by Tab.
    componentDidMount() {
        const button = this.containerRef.current?.querySelector('button')
        if (button) {
            button.focus()
        }
    }

    renderBottomRow() {
        if (this.props.model.bottomRightRow.length === 0) {
            return <></>
        }

        const div = this.props.model.bottomRightRow.map((value, index) => {
            return <>
                {value}
            </>
        })
        return <div
                style={{
                    'textAlign': 'right',
                    'position': 'absolute',
                    'right': '8px',
                    'bottom': '8px'
                }}>
                {div}
            </div>
    }

    render() {
        return (
            <Container innerRef={this.containerRef}>
                <Row><Col><Label style={{
                    'fontSize': '20pt',
                    'fontWeight': '500'
                }}>{this.props.model.title}</Label></Col></Row>
                <Row>
                   <Col>
                        <Container>
                            <p>{this.props.model.message}</p>
                        </Container>
                    </Col>
                </Row>
                <Row>
                    {this.renderBottomRow()}
                </Row>
            </Container>
        )
    }
}

export { AlertModalController, AlertModalModel, errorMessage }

